import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppViewToggle, {
  AppViewToggleOption,
} from '@nxsys/components/AppViewToggle';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useGetTimesheetBreakdown } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { TimesheetItem } from '@nxsys/types/api/timesheetBreakdown';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatDateDisplay } from '@nxsys/utils/dateUtils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Skeleton, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIsAllowedTimesheetCreate } from '@nxsys/hooks/useEmployeePermission';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';

export type ViewMode = 'chart' | 'list';

const EmployeeDashboardTimesheet = () => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const navigate = useNavigateWithQuery();
  const canCreateTimesheet = useIsAllowedTimesheetCreate();

  const [viewMode, setViewMode] = useState<ViewMode>('chart');

  // Fetch timesheet breakdown data
  const {
    value: timesheetBreakdownValue,
    loading,
    error,
    run: fetchTimesheetBreakdown,
  } = useGetTimesheetBreakdown();

  useEffect(() => {
    fetchTimesheetBreakdown();
  }, []);

  // Extract data from API response
  const apiData = timesheetBreakdownValue?.data ?? null;
  const breakdown = apiData?.breakdown ?? null;
  const timesheets = apiData?.timesheets ?? [];

  // Calculate values for chart
  const approvedValue = breakdown?.totalApprovedLatest ?? 0;
  const awaitingApprovalValue = breakdown?.totalAwaitingLatest ?? 0;
  const totalValue = approvedValue + awaitingApprovalValue;

  // Calculate percentage change
  const percentageChange = breakdown?.timesheetPercentageChange;
  const isPositive = percentageChange !== null && Number(percentageChange) >= 0;

  const formattedPercentage =
    percentageChange === null
      ? null
      : Number(percentageChange) > 0
        ? `+${Number(percentageChange).toFixed(2)}%`
        : Number(percentageChange) < 0
          ? `${Number(percentageChange).toFixed(2)}%`
          : '0%';

  const viewModeOptions: AppViewToggleOption[] = [
    {
      icon: 'IconChartBar',
      value: 'chart',
    },
    {
      icon: 'IconDocumentList',
      value: 'list',
    },
  ];

  const handleViewModeChange = (value: string) => {
    setViewMode(value as ViewMode);
  };

  const columns: ColumnsType<TimesheetItem> = [
    {
      key: 'action',
      title: 'Action',
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="flex items-center justify-start gap-x-2">
          <li onClick={() => navigate(`/timesheets-employee/${record.id}`)}>
            <Tooltip title="View">
              <span className="cursor-pointer">
                <Icons.IconEye />
              </span>
            </Tooltip>
          </li>
          {record.file && (
            <li onClick={() => window.open(record.file, '_blank')}>
              <Tooltip title="Download">
                <span className="w-[20px] h-[20px]">
                  <Icons.IconDownload />
                </span>
              </Tooltip>
            </li>
          )}
        </ul>
      ),
      width: 100,
    },
    {
      title: 'Timesheet ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Agency Name',
      dataIndex: 'agencyRef',
      key: 'agencyRef',
      render: (value) => value?.name ?? '-',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      align: 'right',
      render: (value) => formatDateDisplay(value),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      align: 'right',
      render: (value) => formatDateDisplay(value),
    },
    {
      title: 'Timesheets Amount',
      dataIndex: 'totalNet',
      key: 'totalNet',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
  ];

  // Loading state
  if (loading && !apiData) {
    return (
      <AppCard className="h-full">
        <div className="p-[20px]">
          <Skeleton active />
        </div>
      </AppCard>
    );
  }

  // Error state
  if (error && !apiData) {
    return (
      <AppCard className="h-full">
        <div className="p-[20px]">
          <div className="text-[16px] font-[500] mb-[6px]">Timesheet</div>
          <div className="text-center text-neutral-500 py-[32px]">
            Failed to load timesheet data
          </div>
        </div>
      </AppCard>
    );
  }

  return (
    <AppCard className="h-full">
      <div className="p-[20px]">
        <div className="flex items-center justify-between w-full">
          <div className="flex-1">
            <div className="text-[16px] font-[500] mb-[6px]">Timesheet</div>
            <div className="text-[12px] font-[400] text-neutral-500">
              A summary of your submitted timesheets values.
            </div>
          </div>
          <div className="flex items-center gap-x-[8px]">
            <AppViewToggle
              value={viewMode}
              onChange={handleViewModeChange}
              options={viewModeOptions}
            />
            {canCreateTimesheet && (
              <AppButton
                name="Submit Timesheet"
                variant="outlined"
                onClick={() => navigate('/timesheets-employee/add-new')}
              />
            )}
          </div>
        </div>
      </div>
      <div className="mt-[24px] pb-3">
        {viewMode === 'chart' ? (
          <div className="px-[20px] pb-[20px]">
            <div className="flex items-center gap-x-[8px]">
              <div className="text-[18px] font-[600] text-neutral-700">
                {formatMoney(totalValue, showCurrencyByCountry(payrollCountry))}
              </div>
              {formattedPercentage !== null && (
                <div
                  className={`flex gap-x-[4px] items-center border ${
                    isPositive
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-red-300 bg-red-50'
                  } rounded-full p-[4px]`}
                >
                  {isPositive && (
                    <Icons.IconArrowUp
                      width={12}
                      height={12}
                      color={defaultTheme.theme.palette.primary[600]}
                    />
                  )}
                  <span
                    className={`text-[12px] font-[400] ${
                      isPositive ? 'text-primary-600' : 'text-red-600'
                    }`}
                  >
                    {formattedPercentage} to last period
                  </span>
                </div>
              )}
            </div>
            <div className="text-[14px] font-[400] text-neutral-500 mt-[4px]">
              Total Timesheet Value
            </div>
            <div className="flex items-center gap-x-[6px] my-[16px]">
              <div
                className="h-[8px] bg-primary-600 rounded-[2px]"
                style={{
                  width: `${(approvedValue / (approvedValue + awaitingApprovalValue)) * 100}%`,
                }}
              ></div>
              <div
                className="h-[8px] bg-warning-300 rounded-[2px]"
                style={{
                  width: `${(awaitingApprovalValue / (approvedValue + awaitingApprovalValue)) * 100}%`,
                }}
              ></div>
            </div>
            <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-x-[16px]">
              <div className="border border-[#CDDDD9] rounded-[8px] py-[12px] px-[16px]">
                <div className="flex gap-x-[8px]">
                  <div className="w-[12px] h-[12px] bg-primary-600 rounded-[2px]" />
                  <div>
                    <div className="text-[16px] font-[500] text-neutral-700">
                      {formatMoney(
                        approvedValue,
                        showCurrencyByCountry(payrollCountry),
                      )}
                    </div>
                    <div className="text-[12px] font-[400] text-neutral-500">
                      Approved Value
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-[#CDDDD9] rounded-[8px] py-[12px] px-[16px]">
                <div className="flex gap-x-[8px]">
                  <div className="w-[12px] h-[12px] bg-warning-300 rounded-[2px]" />
                  <div>
                    <div className="text-[16px] font-[500] text-neutral-700">
                      {formatMoney(
                        awaitingApprovalValue,
                        showCurrencyByCountry(payrollCountry),
                      )}
                    </div>
                    <div className="text-[12px] font-[400] text-neutral-500">
                      Awaiting Approval Value
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <AppTableContainer
              rowKey="id"
              data={timesheets}
              loading={loading}
              page={1}
              total={timesheets.length}
              pageSize={10}
              pagination={false}
              scroll={{ x: 1200, y: 400 }}
              columns={columns}
            />
          </>
        )}
      </div>
    </AppCard>
  );
};

export default EmployeeDashboardTimesheet;
